import { formatPercent, weightGreen, weightRed } from '@3fourteen/core';
import { flexRender, Row } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { classNames } from 'helpers/classNames';
import { memo } from 'react';

import { AntIcon } from 'components';

import { FullCycleRow } from '../FullCycleTable/columns';

interface FullCycleTableRowProps {
  row: Row<FullCycleRow>;
  inactiveRow?: boolean;
}

function FullCycleTableRow({ row, inactiveRow = false }: FullCycleTableRowProps) {
  return (
    <tr
      className={classNames(
        inactiveRow ? 'opacity-80 italic' : undefined,
        'border-y border-solid border-zinc-100 even:bg-zinc-50'
      )}>
      {row.getVisibleCells().map((cell) => {
        const colId = cell.column.id;
        const isNew = row.original.is_new;
        const isOld = row.original.is_old;
        const tickerClass = isNew ? 'bg-sage-500' : isOld ? 'bg-terracotta-500' : '';
        const isDateCell = colId === 'date' || colId === 'inactive_date';
        const isWeightCell = colId === 'pct_change' || colId === 'sp500_change';
        const isTickerCell = colId === 'ticker';
        const isPctChangeCell = colId === 'pct_change';
        const isSectorCell = colId === 'sector';
        const isCompanyCell = colId === 'company_name';
        const isRankCell = colId === 'ranking';
        const weightCellClass = isPctChangeCell ? 'font-semibold' : undefined;
        const width =
          isWeightCell || isTickerCell
            ? '10%'
            : isSectorCell
            ? '16%'
            : isCompanyCell
            ? '17%'
            : isRankCell
            ? '9%'
            : '14%';

        return (
          <td
            role='cell'
            key={cell.id}
            width={width}
            data-testid={`td-${colId}`}
            className='first:pl-4 md:first:pl-6 border-r border-solid border-zinc-100 px-3 py-0 text-15 whitespace-nowrap'>
            {isDateCell && (
              <span className='text-13 uppercase py-1 inline-block'>
                {cell.getValue() ? dayjs(cell.getValue() as string).format('MMMM D, YYYY') : ''}
              </span>
            )}
            {isTickerCell && (
              <div
                className={
                  isNew || isOld
                    ? classNames(
                        tickerClass,
                        'text-white font-semibold inline-block  px-0 w-16 text-center rounded-sm'
                      )
                    : ''
                }>
                {cell.getValue() as string}
              </div>
            )}
            {(isSectorCell || isCompanyCell || isRankCell) &&
              flexRender(cell.column.columnDef.cell, cell.getContext())}
            {isWeightCell && (
              <span className={`inline-block w-12 ${weightCellClass}`}>
                {formatPercent(cell.getValue() as number)}
              </span>
            )}
            {isPctChangeCell && (cell.getValue() as number) !== 0 && (
              <span className='not-italic'>
                <AntIcon
                  name={(cell.getValue() as number) > 0 ? 'caretup' : 'caretdown'}
                  color={(cell.getValue() as number) > 0 ? weightGreen : weightRed}
                />
              </span>
            )}
          </td>
        );
      })}
    </tr>
  );
}
export default memo(FullCycleTableRow);
